import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { ProposalGenerator } from './ProposalGenerator';
import './Proposals.css';

// Status helpers moved outside to avoid re-creation and ensure they are defined
const statusTones = {
    draft: { bg: '#f5f3ff', color: '#7c3aed', dot: '#8b5cf6', label: 'Generated' }, // Purple
    sent: { bg: '#eff6ff', color: '#2563eb', dot: '#3b82f6', label: 'Sent' },
    viewed: { bg: '#fdf4ff', color: '#c026d3', dot: '#d946ef', label: 'Viewed' },
    accepted: { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Accepted' },
    rejected: { bg: '#fef2f2', color: '#dc2626', dot: '#ef4444', label: 'Rejected' },
    expired: { bg: '#fff7ed', color: '#d97706', dot: '#f59e0b', label: 'Expired' },
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const SHARED_STYLE = `
    @keyframes proposals-skeleton-shimmer {
        0% { background-position: 100% 0; }
        100% { background-position: -100% 0; }
    }
    .proposals-skeleton {
        background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
        background-size: 400% 100%;
        animation: proposals-skeleton-shimmer 1.4s ease infinite;
    }
    @keyframes proposals-bubble-float-large {
        0% { transform: translate(0px, 0px) scale(1); }
        30% { transform: translate(-5px, -7px) scale(1.04); }
        60% { transform: translate(4px, -4px) scale(0.97); }
        100% { transform: translate(0px, 0px) scale(1); }
    }
    @keyframes proposals-bubble-float-small {
        0% { transform: translate(0px, 0px) scale(1); }
        35% { transform: translate(6px, -9px) scale(1.06); }
        65% { transform: translate(-4px, -3px) scale(0.95); }
        100% { transform: translate(0px, 0px) scale(1); }
    }
    .proposals-metrics-grid > div {
        position: relative;
        overflow: hidden;
        isolation: isolate;
    }
    .proposals-metrics-grid > div::before {
        content: '';
        position: absolute;
        z-index: 0;
        width: 90px;
        height: 90px;
        border-radius: 50%;
        background: #667eea;
        opacity: 0.06;
        right: -26px;
        bottom: -26px;
        pointer-events: none;
        animation: proposals-bubble-float-large 5.5s ease-in-out infinite;
    }
    .proposals-metrics-grid > div::after {
        content: '';
        position: absolute;
        z-index: 0;
        width: 54px;
        height: 54px;
        border-radius: 50%;
        background: #667eea;
        opacity: 0.04;
        right: 36px;
        bottom: -20px;
        pointer-events: none;
        animation: proposals-bubble-float-small 4.2s ease-in-out infinite;
    }
    .proposals-metrics-grid > div:nth-child(1)::before,
    .proposals-metrics-grid > div:nth-child(1)::after { background: #667eea; }
    .proposals-metrics-grid > div:nth-child(2)::before,
    .proposals-metrics-grid > div:nth-child(2)::after { background: #1EA7FF; }
    .proposals-metrics-grid > div:nth-child(3)::before,
    .proposals-metrics-grid > div:nth-child(3)::after { background: #3b82f6; }
    .proposals-metrics-grid > div:nth-child(4)::before,
    .proposals-metrics-grid > div:nth-child(4)::after { background: #16a34a; }
    
    .prop-action-btn { 
        width: 30px; height: 30px; border-radius: 8px; border: none; background: transparent; 
        cursor: pointer; display: flex; align-items: center; justify-content: center; 
        transition: background .15s; 
    }
    .prop-btn-primary { 
        padding: 9px 20px; border: none; border-radius: 10px; 
        background: linear-gradient(135deg,#056098 0%,#56a07a 100%); 
        color: #fff; font-size: 12.5px; font-weight: 700; cursor: pointer; 
        font-family: 'Inter',sans-serif; transition: opacity .15s; 
        display: flex; align-items: center; gap: 6px; 
    }
    .prop-btn-primary:hover { opacity: .88; }
    
    .prop-tbl thead th { 
        font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; 
        letter-spacing: .06em; padding: 10px 16px; text-align: left; white-space: nowrap; 
    }
    .prop-tbl tbody td { font-size: 13px; color: #374151; padding: 12px 16px; vertical-align: middle; }
    .prop-tbl tbody tr { border-bottom: 1px solid #f3f4f6; transition: background .12s; }
    .prop-tbl tbody tr:hover { background: #f9fafb; }
`;

function Proposals() {
    const { hasPermission, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [proposals, setProposals] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
    const [editingProposalId, setEditingProposalId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(10);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [initialTab, setInitialTab] = useState('form');

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setActiveDropdown(null);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    // Reset page to 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchProposals = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/proposals');
            setProposals(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching proposals:', error);
            setLoading(false);
        }
    }, []);

    const hasAccess = hasPermission('proposals', 'read') || hasPermission('proposals', 'create') ||
        hasPermission('proposals', 'update') || hasPermission('proposals', 'delete');

    useEffect(() => {
        if (hasAccess) {
            fetchProposals();
        }
    }, [fetchProposals, hasAccess]);

    // Handle auto-open generator if passed via location state
    useEffect(() => {
        if (location.state?.createProposal && hasPermission('proposals', 'create')) {
            setIsGeneratorOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [location.state, hasPermission]);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this proposal?')) {
            try {
                await api.delete(`/proposals/${id}`);
                fetchProposals();
                alert('Proposal deleted successfully');
            } catch (error) {
                alert('Error deleting proposal');
            }
        }
    };

    const handleEdit = (id) => {
        setEditingProposalId(id);
        setIsReadOnly(false);
        setInitialTab('form');
        setIsGeneratorOpen(true);
    };

    const handleView = (id) => {
        setEditingProposalId(id);
        setIsReadOnly(true);
        setInitialTab('preview');
        setIsGeneratorOpen(true);
    };

    const handleStatusChange = async (id, newStatus) => {
        // Optimistic update
        const originalProposals = [...proposals];
        setProposals(prev => prev.map(p => p._id === id ? { ...p, status: newStatus } : p));

        try {
            await api.patch(`/proposals/${id}/status`, { status: newStatus });
            // Optionally fetch in background to ensure sync
            // fetchProposals(); 
        } catch (error) {
            setProposals(originalProposals);
            alert('Error updating status');
        }
    };

    const downloadPDF = async (id) => {
        try {
            const response = await api.get(`/proposals/${id}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `proposal-${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Error downloading PDF');
        }
    };

    const filteredProposals = proposals.filter(p =>
        p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.clientEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.proposalNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Access Control Check (Hooks above this!)
    if (!hasAccess) {
        return (
            <div className="page-container" style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
                <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb', padding: '48px', textAlign: 'center', maxWidth: '420px', margin: '60px auto' }}>
                    <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>Access Denied</div>
                    <p style={{ fontSize: '13px', color: '#9ca3af' }}>Only Admin and HR personnel can access the Proposals section.</p>
                </div>
            </div>
        );
    }

    const totalProposals = proposals.length;
    const draftCount = proposals.filter(p => p.status === 'draft').length;
    const sentCount = proposals.filter(p => p.status === 'sent').length;
    const acceptedCount = proposals.filter(p => p.status === 'accepted').length;

    // Pagination logic
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentProposals = filteredProposals.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredProposals.length / recordsPerPage);

    if (loading) {
        return (
            <div className="page-container" style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
                <style>{SHARED_STYLE}</style>
                <div style={{ textAlign: 'center', padding: '100px', color: '#6b7280' }}>Loading proposals...</div>
            </div>
        );
    }

    // List View
    return (
        <div className="page-container" style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
            <style>{SHARED_STYLE}</style>

            {/* Page Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#111827', letterSpacing: '-0.02em' }}>Proposals</div>
                    <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '2px' }}>Create and manage client proposals</div>
                </div>
                {hasPermission('proposals', 'create') && (
                    <button className="prop-btn-primary" onClick={() => { setEditingProposalId(null); setIsReadOnly(false); setInitialTab('form'); setIsGeneratorOpen(true); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Generate Proposal
                    </button>
                )}
            </div>

            {/* Metric Cards */}
            <div className="proposals-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                {[
                    { label: 'Total Proposals', value: totalProposals, accent: '#667eea', note: 'Across all clients', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg> },
                    { label: 'Generated', value: draftCount, accent: '#7c3aed', note: 'To be refined', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg> },
                    { label: 'Sent', value: sentCount, accent: '#3b82f6', note: 'Awaiting response', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2 11 13" /><path d="M22 2 15 22 11 13 2 9 22 2z" /></svg> },
                    { label: 'Accepted', value: acceptedCount, accent: '#16a34a', note: 'Won proposals', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg> },
                ].map(({ label, value, accent, note, icon }) => (
                    <div key={label} style={{ background: '#fff', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '13px', color: '#64748b' }}>{label}</span>
                            <div style={{ color: accent }}>{icon}</div>
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>{value}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{note}</div>
                    </div>
                ))}
            </div>

            {/* Table card */}
            <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '9px', padding: '8px 12px', flex: 1, maxWidth: '360px' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                        <input type="text" placeholder="Search proposals…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', background: 'transparent', width: '100%' }} />
                    </div>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{filteredProposals.length} Results</span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table className="prop-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Client</th>
                                <th>Total</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Generated By</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProposals.length === 0 ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No proposals found</td></tr>
                            ) : (
                                currentProposals.map(proposal => {
                                    const st = statusTones[proposal.status] || statusTones.draft;
                                    return (
                                        <tr key={proposal._id}>
                                            <td style={{ fontWeight: 600, color: '#056098' }}>{proposal.proposalNumber}</td>
                                            <td style={{ fontWeight: 600 }}>{proposal.title}</td>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{proposal.clientName}</div>
                                                <div style={{ fontSize: '11px', color: '#6b7280' }}>{proposal.clientEmail}</div>
                                            </td>
                                            <td style={{ fontWeight: 600 }}>₹{proposal.total?.toLocaleString()}</td>
                                            <td>{fmt(proposal.date)}</td>
                                            <td>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    background: st.bg,
                                                    color: st.color,
                                                    borderRadius: '6px',
                                                    padding: '4px 10px',
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.02em',
                                                    border: `1px solid ${st.dot}20`
                                                }}>
                                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: st.dot }} />
                                                    {st.label || proposal.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#475569' }}>
                                                        {proposal.createdBy?.firstName?.[0] || 'U'}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: '12.5px' }}>
                                                            {proposal.createdBy ? `${proposal.createdBy.firstName} ${proposal.createdBy.lastName}` : 'System'}
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: '#7c3aed', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                            {proposal.createdBy?.role?.name || 'Staff'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', position: 'relative' }}>
                                                    <button className="prop-action-btn" onClick={() => handleView(proposal._id)} title="View">
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                    </button>
                                                    {hasPermission('proposals', 'update') && (
                                                        <button className="prop-action-btn" onClick={() => downloadPDF(proposal._id)} title="Download">
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                                        </button>
                                                    )}
                                                    {(hasPermission('proposals', 'update') || hasPermission('proposals', 'delete')) && (
                                                        <div style={{ position: 'relative' }}>
                                                            <button className="prop-action-btn" onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === proposal._id ? null : proposal._id); }}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                                                            </button>
                                                            {activeDropdown === proposal._id && (
                                                                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: '#fff', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', zIndex: 100, minWidth: '120px', padding: '4px' }}>
                                                                    {hasPermission('proposals', 'update') && <button onClick={() => handleEdit(proposal._id)} style={{ width: '100%', textAlign: 'left', padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px' }}>Edit</button>}
                                                                    {hasPermission('proposals', 'update') && proposal.status === 'draft' && <button onClick={() => handleStatusChange(proposal._id, 'sent')} style={{ width: '100%', textAlign: 'left', padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', color: '#2563eb' }}>Mark as Sent</button>}
                                                                    {hasPermission('proposals', 'delete') && proposal.status !== 'accepted' && proposal.status !== 'rejected' && <button onClick={() => handleDelete(proposal._id)} style={{ width: '100%', textAlign: 'left', padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', color: '#dc2626' }}>Delete</button>}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div style={{ padding: '16px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Page {currentPage} of {totalPages}</span>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>Prev</button>
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
                        </div>
                    </div>
                )}
            </div>

            {/* Proposal Generator Modal */}
            <ProposalGenerator
                isOpen={isGeneratorOpen}
                proposalId={editingProposalId}
                initialTab={initialTab}
                readOnly={isReadOnly}
                onClose={() => {
                    setIsGeneratorOpen(false);
                    setEditingProposalId(null);
                    setIsReadOnly(false);
                }}
                onSuccess={(data) => {
                    setIsGeneratorOpen(false);
                    setEditingProposalId(null);
                    setIsReadOnly(false);
                    if (data) {
                        setProposals(prev => {
                            const exists = prev.find(p => p._id === data._id);
                            if (exists) return prev.map(p => p._id === data._id ? data : p);
                            return [data, ...prev];
                        });
                    } else {
                        fetchProposals();
                    }
                }}
            />
        </div>
    );
}

export default Proposals;
